'use client'

import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { Container, Flex } from '@quark/layout'
import { Reveal } from '@quark/animation'
import { Text } from '@quark/typography'
import isMobile from 'ismobilejs'
import styles from './brandsCarousal.module.scss'

interface BrandImage {
  url?: string
}

interface UspItem {
  title?: string
  subtitle?: string
  iconUrl?: {
    url: string
  }
  mobileIconUrl?: {
    url: string
  }
}

interface BrandsCarousalProps {
  title?: string
  brandImageList?: BrandImage[]
  uspList?: UspItem[]
}

const BrandsCarousal = (data: BrandsCarousalProps) => {
  const [shouldScroll, setShouldScroll] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const isMobileDevice = isMobile()?.phone

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current && trackRef.current) {
        const containerWidth = containerRef.current.clientWidth
        const scrollWidth = trackRef.current.scrollWidth
        const hasDuplicates = trackRef.current.children.length > (data?.brandImageList?.filter(b => b?.url).length || 0)
        const originalListWidth = hasDuplicates ? scrollWidth / 2 : scrollWidth
        setShouldScroll(originalListWidth > containerWidth + 5)
      }
    }

    const checkMultipleTimes = () => {
      checkOverflow()
      setTimeout(checkOverflow, 300)
      setTimeout(checkOverflow, 600)
    }

    const timeoutId = setTimeout(checkMultipleTimes, 100)

    let resizeTimeout: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(checkOverflow, 150)
    }
    window.addEventListener('resize', handleResize)

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(checkOverflow, 100)
    })
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }
    if (trackRef.current) {
      resizeObserver.observe(trackRef.current)
    }

    const imageCheckTimeout = setTimeout(() => {
      const images = trackRef.current?.querySelectorAll('img')
      images?.forEach(img => {
        if (!img.complete) {
          img.addEventListener('load', checkOverflow, { once: true })
        } else {
          checkOverflow()
        }
      })
    }, 200)

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(resizeTimeout)
      clearTimeout(imageCheckTimeout)
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
    }
  }, [data?.brandImageList])

  return (
    <Container type="fluid" backgroundColor="base/elevated" paddingTop="spacing-40" paddingBottom="spacing-40">
      <Container type="max-parent" paddingBottom="spacing-24">
        <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 100 }}>
          <Text
            as="p"
            fontStyle={{ lg: 'um_text-2xl/normal', md: 'um_text-xl/normal', sm: 'um_text-lg/normal' }}
            textAlign="center"
            color="base/primary">
            <span dangerouslySetInnerHTML={{ __html: data?.title || '' }} />
          </Text>
        </Reveal>
      </Container>
      <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 200 }}>
        <Container type="max-parent">
          <div className={styles.uspGrid}>
            {data?.uspList?.map((usp: UspItem, index: number) => (
              <div key={`usp-${index}`} className={styles.uspItem}>
                <Flex alignItems="center" gap="spacing-12">
                  <Flex flexDirection="column" gap="spacing-4">
                    {usp?.title && <div className={styles.uspTitle}>{usp.title}</div>}
                    {usp?.subtitle && (
                      <Text
                        as="p"
                        fontStyle={{
                          lg: 'um_text-lg/normal',
                          md: 'um_text-base/normal',
                          sm: 'um_text-sm/normal',
                        }}
                        color="base/secondary">
                        {usp.subtitle}
                      </Text>
                    )}
                  </Flex>
                </Flex>
              </div>
            ))}
          </div>
        </Container>
      </Reveal>
      <Reveal as="span" animationProps={{ threshold: 0.1, animationDelayMs: 200 }}>
        <Container type="fluid" overflow="hidden" ref={containerRef}>
          <div ref={trackRef} className={`${styles.carouselTrack} ${shouldScroll ? styles.scroll : ''}`}>
            {data?.brandImageList?.map((brand: BrandImage, index: number) => (
              <PropertyControlledComponent controllerProperty={brand?.url} fallback={null}>
                <div key={`first-${index}`} className={styles.logoContainer}>
                  <Image
                    src={brand.url}
                    alt={`Brand logo ${index + 1}`}
                    width={isMobileDevice ? 80 : 120}
                    height={isMobileDevice ? 50 : 60}
                    className={styles.logoImage}
                    loading="lazy"
                  />
                </div>
              </PropertyControlledComponent>
            ))}
            {shouldScroll &&
              data?.brandImageList?.map((brand: BrandImage, index: number) => (
                <PropertyControlledComponent controllerProperty={brand?.url} fallback={null}>
                  <div key={`second-${index}`} className={styles.logoContainer}>
                    <Image
                      src={brand.url}
                      alt={`Brand logo ${index + 1}`}
                      width={isMobileDevice ? 80 : 120}
                      height={isMobileDevice ? 50 : 60}
                      className={styles.logoImage}
                      loading="lazy"
                    />
                  </div>
                </PropertyControlledComponent>
              ))}
          </div>
        </Container>
      </Reveal>
    </Container>
  )
}

export default BrandsCarousal
